import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, MousePointerClick, TrendingUp, Euro, Users, Edit2, Webhook, ExternalLink } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, Cell } from "recharts";

const RANGES = [
  { label: "7 jours", days: 7 },
  { label: "30 jours", days: 30 },
  { label: "90 jours", days: 90 },
];

export default function AdminAffiliate() {
  const { user, loading: authLoading } = useAuth();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [topParts, setTopParts] = useState([]);
  const [topModels, setTopModels] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [conversions, setConversions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMonth, setEditMonth] = useState(null);   // {period_year, period_month, ...}
  const [editForm, setEditForm] = useState({ clicks_reported: "", commissions_eur: "", orders_count: "", notes: "" });

  const loadAll = () => {
    if (authLoading || !user || user.role !== "admin") return;
    setLoading(true);
    Promise.all([
      api.get(`/admin/affiliate/overview`, { params: { days } }),
      api.get(`/admin/affiliate/timeseries`, { params: { days } }),
      api.get(`/admin/affiliate/top-parts`, { params: { days, limit: 10 } }),
      api.get(`/admin/affiliate/top-models`, { params: { days, limit: 10 } }),
      api.get(`/admin/affiliate/breakdown`, { params: { days } }),
      api.get(`/admin/affiliate/monthly-report`, { params: { months: 12 } }),
      api.get(`/admin/affiliate/conversions`, { params: { days: 90 } }).catch(() => ({ data: null })),
    ])
      .then(([o, ts, tp, tm, br, mr, cv]) => {
        setOverview(o.data);
        setSeries(ts.data.series || []);
        setTopParts(tp.data.items || []);
        setTopModels(tm.data.items || []);
        setBreakdown(br.data.items || []);
        setMonthly(mr.data.items || []);
        setConversions(cv.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, [days, user, authLoading]);

  const openEdit = (m) => {
    setEditMonth(m);
    setEditForm({
      clicks_reported: m.recorded_clicks ?? m.clicks ?? "",
      commissions_eur: m.recorded_commission_eur ?? "",
      orders_count: m.recorded_orders ?? "",
      notes: m.notes || "",
    });
  };

  const saveCommission = async () => {
    try {
      await api.post("/admin/affiliate/commissions", {
        period_year: editMonth.period_year,
        period_month: editMonth.period_month,
        partner: "tubs.fr",
        clicks_reported: parseInt(editForm.clicks_reported || 0),
        commissions_eur: parseFloat(editForm.commissions_eur || 0),
        orders_count: parseInt(editForm.orders_count || 0),
        notes: editForm.notes || null,
      });
      toast.success("Réconciliation enregistrée");
      setEditMonth(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (authLoading || !user) return null;
  if (user.role !== "admin") {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-center">
        <h1 className="text-2xl font-bold">Accès refusé</h1>
        <p className="mt-2 text-muted-foreground">Cette page est réservée aux administrateurs.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="admin-affiliate-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Admin · Monétisation</div>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tighter">
            Analytics <span className="text-primary">affiliés tubs.fr</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Suivi des clics sortants + estimation des commissions générées.
          </p>
        </div>
        <div className="flex gap-2" data-testid="range-selector">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                days === r.days
                  ? "bg-secondary text-white shadow-md"
                  : "bg-black/5 hover:bg-black/10 text-foreground"
              }`}
              data-testid={`range-btn-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !overview ? (
        <div className="mt-12 flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
            <KpiCard label="Clics période" value={overview.clicks_total} icon={MousePointerClick} color="text-primary" testid="kpi-clicks" />
            <KpiCard label="Clics tout temps" value={overview.clicks_all_time} icon={TrendingUp} color="text-secondary" testid="kpi-clicks-all" />
            <KpiCard label="Visiteurs uniques" value={overview.unique_visitors} icon={Users} color="text-emerald-700" testid="kpi-unique" />
            <KpiCard
              label="Commission estimée"
              value={`${overview.estimated.commission_eur.toFixed(2)} €`}
              icon={Euro}
              color="text-amber-700"
              hint={`(${overview.estimated.orders} commandes × ${overview.assumptions.average_order_eur}€ × ${(overview.assumptions.commission_rate * 100).toFixed(0)}%)`}
              testid="kpi-commission"
            />
          </div>

          {/* Timeseries chart */}
          <Card className="mt-8 p-6" data-testid="timeseries-chart">
            <h2 className="font-display text-xl font-bold tracking-tight">Clics par jour</h2>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="clicks" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Top parts + Breakdown */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6" data-testid="top-parts">
              <h2 className="font-display text-xl font-bold tracking-tight">Top pièces cliquées</h2>
              {topParts.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">Aucun clic sur la période.</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {topParts.map((p, i) => (
                    <div
                      key={`${p.part_type}-${p.brand}-${p.part_ref}-${i}`}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-black/5"
                      data-testid={`top-part-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px] rounded-full">{p.part_type}</Badge>
                          <span className="text-sm font-semibold truncate">{p.brand}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground truncate">{p.part_ref}</div>
                      </div>
                      <span className="text-sm font-bold text-primary shrink-0">{p.clicks}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-6" data-testid="breakdown-context">
              <h2 className="font-display text-xl font-bold tracking-tight">Origine des clics</h2>
              {breakdown.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">Aucune donnée.</p>
              ) : (
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={breakdown} layout="vertical" margin={{ left: 100 }}>
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="context" type="category" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="clicks" radius={[0, 6, 6, 0]}>
                        {breakdown.map((entry, i) => (
                          <Cell key={i} fill={["#2563eb", "#f59e0b", "#10b981", "#8b5cf6"][i % 4]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {/* Top models */}
          {topModels.length > 0 && (
            <Card className="mt-6 p-6" data-testid="top-models">
              <h2 className="font-display text-xl font-bold tracking-tight">Top modèles générateurs de clics</h2>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {topModels.map((m, i) => (
                  <div key={m.model_slug} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-black/5" data-testid={`top-model-${i}`}>
                    <span className="text-sm font-semibold truncate">{m.model_slug}</span>
                    <span className="text-sm font-bold text-primary shrink-0">{m.clicks} clics</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Assumptions info */}
          <Card className="mt-6 p-6 bg-primary/5 border-primary/20 text-sm" data-testid="assumptions">
            <h3 className="font-semibold">Hypothèses du calcul de commission</h3>
            <ul className="mt-3 space-y-1 text-muted-foreground text-xs">
              <li>• Taux de conversion clic → commande : <b>{(overview.assumptions.conversion_rate * 100).toFixed(1)}%</b> (configurable via <code>TUBS_CONVERSION_RATE</code>)</li>
              <li>• Panier moyen tubs.fr : <b>{overview.assumptions.average_order_eur} €</b> (configurable via <code>TUBS_AVERAGE_ORDER_EUR</code>)</li>
              <li>• Taux de commission affilié : <b>{(overview.assumptions.commission_rate * 100).toFixed(0)}%</b> (configurable via <code>TUBS_COMMISSION_RATE</code>)</li>
              <li className="mt-2">💡 Ces valeurs sont des estimations. Le CA réel sera confirmé par le rapport mensuel tubs.fr.</li>
            </ul>
          </Card>

          {/* Conversions réelles (postback partenaire) */}
          <Card className="mt-6 p-6 border-emerald-200 bg-emerald-50/40" data-testid="conversions-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-emerald-600" /> Conversions réelles — postback tubs.fr
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Commandes remontées automatiquement par tubs.fr (90 derniers jours).
                </p>
              </div>
              <a href="/partenaires/tubs" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline" data-testid="link-partner-doc">
                Doc d'intégration partenaire <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {conversions && conversions.kpi.orders > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white border border-black/5 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Commandes</div>
                    <div className="mt-1 font-display text-2xl font-black text-emerald-700" data-testid="conv-kpi-orders">{conversions.kpi.orders}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-black/5 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">CA généré</div>
                    <div className="mt-1 font-display text-2xl font-black" data-testid="conv-kpi-revenue">{conversions.kpi.revenue_eur.toFixed(2)} €</div>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-black/5 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Commissions</div>
                    <div className="mt-1 font-display text-2xl font-black text-amber-700" data-testid="conv-kpi-commission">{conversions.kpi.commission_eur.toFixed(2)} €</div>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-3">Commande</th>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-right py-2 px-3">Total</th>
                        <th className="text-right py-2 px-3">Commission</th>
                        <th className="text-right py-2 pl-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversions.items.map((c) => (
                        <tr key={c.id} className="border-b border-black/5" data-testid={`conversion-${c.order_id}`}>
                          <td className="py-2 pr-3 font-mono font-semibold">{c.order_id}</td>
                          <td className="py-2 px-3 text-muted-foreground">{c.occurred_at && new Date(c.occurred_at).toLocaleDateString("fr-FR")}</td>
                          <td className="text-right py-2 px-3">{c.order_total_eur.toFixed(2)} €</td>
                          <td className="text-right py-2 px-3 font-bold text-emerald-700">{c.commission_eur.toFixed(2)} €</td>
                          <td className="text-right py-2 pl-3">
                            <Badge className={`rounded-full border-0 text-[10px] ${c.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                              {c.status === "confirmed" ? "Confirmée" : "Annulée"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="mt-4 p-4 rounded-lg bg-white border border-dashed border-emerald-300 text-sm text-muted-foreground" data-testid="conversions-empty">
                Aucune conversion remontée pour le moment. L'endpoint <code className="font-mono text-xs bg-black/5 px-1.5 py-0.5 rounded">POST /api/affiliate/postback</code> est
                prêt — envoyez la <a href="/partenaires/tubs" target="_blank" rel="noopener noreferrer" className="text-primary underline">doc d'intégration</a> à tubs.fr avec la clé secrète pour activer le suivi automatique.
              </div>
            )}
          </Card>

          {/* Monthly reconciliation report */}
          <Card className="mt-6 p-6" data-testid="monthly-report">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight">Rapport mensuel — Réconciliation commissions</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Comparez les commissions estimées (basées sur les clics enregistrés) avec les commissions réellement versées par tubs.fr.
                </p>
              </div>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="text-left py-2 pr-3">Mois</th>
                    <th className="text-right py-2 px-3">Clics</th>
                    <th className="text-right py-2 px-3">Estimation €</th>
                    <th className="text-right py-2 px-3">Réel €</th>
                    <th className="text-right py-2 px-3">Δ écart</th>
                    <th className="text-right py-2 pl-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {monthly.map((m) => {
                    const delta = m.reconciliation_delta_eur;
                    const deltaClass = delta === null ? "text-muted-foreground" : delta >= 0 ? "text-emerald-600" : "text-red-600";
                    return (
                      <tr key={m.period_label} className="border-b hover:bg-slate-50" data-testid={`month-row-${m.period_label}`}>
                        <td className="py-2 pr-3 font-mono">{m.period_label}</td>
                        <td className="text-right py-2 px-3">{m.clicks}</td>
                        <td className="text-right py-2 px-3 text-muted-foreground">{m.estimated_commission_eur.toFixed(2)}</td>
                        <td className="text-right py-2 px-3 font-bold">
                          {m.recorded_commission_eur !== null ? `${m.recorded_commission_eur.toFixed(2)}` : <span className="text-muted-foreground italic">non saisi</span>}
                        </td>
                        <td className={`text-right py-2 px-3 font-semibold ${deltaClass}`}>
                          {delta !== null ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}` : "—"}
                        </td>
                        <td className="text-right py-2 pl-3">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(m)} className="h-7 px-2 text-xs" data-testid={`edit-month-${m.period_label}`}>
                            <Edit2 className="h-3 w-3 mr-1" /> Saisir
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Dialog open={!!editMonth} onOpenChange={(o) => !o && setEditMonth(null)}>
            <DialogContent data-testid="edit-commission-dialog">
              <DialogHeader>
                <DialogTitle>Réconciliation {editMonth?.period_label}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="clicks_reported">Clics rapportés par tubs.fr</Label>
                  <Input id="clicks_reported" type="number" min="0" value={editForm.clicks_reported} onChange={(e) => setEditForm({ ...editForm, clicks_reported: e.target.value })} data-testid="input-clicks-reported" />
                </div>
                <div>
                  <Label htmlFor="orders_count">Nombre de commandes</Label>
                  <Input id="orders_count" type="number" min="0" value={editForm.orders_count} onChange={(e) => setEditForm({ ...editForm, orders_count: e.target.value })} data-testid="input-orders" />
                </div>
                <div>
                  <Label htmlFor="commissions_eur">Commissions réelles (€)</Label>
                  <Input id="commissions_eur" type="number" min="0" step="0.01" value={editForm.commissions_eur} onChange={(e) => setEditForm({ ...editForm, commissions_eur: e.target.value })} data-testid="input-commissions" />
                </div>
                <div>
                  <Label htmlFor="notes">Notes (optionnel)</Label>
                  <Textarea id="notes" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="input-notes" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMonth(null)}>Annuler</Button>
                <Button onClick={saveCommission} data-testid="btn-save-commission">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, hint, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className={`mt-2 font-display text-3xl font-black ${color}`}>{value}</div>
          {hint && <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div>}
        </div>
        <Icon className={`h-6 w-6 shrink-0 ${color} opacity-60`} />
      </div>
    </Card>
  );
}
